import { useEffect, useMemo, useState } from "react";
import { Drawer } from "antd";
import { Link } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";

export const assessmentProcesses = [
  {
    id: "ap-new-mj-2024",
    name: "New patient assessment process",
    patientType: "NEW",
    resident: "Margaret Johnson",
    startDate: "2024-05-06",
    endDate: "2024-06-05",
    status: "In progress",
    socialWorker: "Amanda Parker",
    attachedRecords: ["Initial Assessment", "Meet and Greet", "Client Onboarding", "Resident 30-Day Probation", "Preliminary Plan", "Annual Residency Progress", "Negotiated Plan"],
  },
  {
    id: "ap-resident-mr-2024",
    name: "Resident annual assessment process",
    patientType: "RESIDENT",
    resident: "Mary Reynolds",
    startDate: "2024-05-12",
    endDate: "2024-06-12",
    status: "Review due",
    socialWorker: "Amanda Parker",
    attachedRecords: ["Initial Assessment Review", "Annual Residency Progress", "Negotiated Plan", "Final Assessment"],
  },
  {
    id: "ap-resident-jl-2024",
    name: "Resident change review",
    patientType: "RESIDENT",
    resident: "James Lee",
    startDate: "2024-04-22",
    endDate: "2024-05-18",
    status: "Confirmed",
    socialWorker: "Martin Lee",
    attachedRecords: ["Initial Assessment Review", "Annual Residency Progress", "Negotiated Plan", "Final Assessment", "Residency Confirmation"],
  },
  {
    id: "ap-new-as-2024",
    name: "New patient assessment process",
    patientType: "NEW",
    resident: "Aisha Sharma",
    startDate: "2024-03-28",
    endDate: "2024-04-27",
    status: "Closed",
    socialWorker: "Rachel Kim",
    attachedRecords: ["Initial Assessment", "Meet and Greet", "Client Onboarding", "Preliminary Plan", "Annual Residency Progress", "Negotiated Plan", "Final Assessment"],
  },
];

export const processStatusClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("confirmed") || normalized.includes("closed")) return "low";
  if (normalized.includes("due")) return "high";
  return "blue";
};

export const formatDate = (date) =>
  date ? new Date(`${date}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Not set";

export const processResidentName = (process) =>
  process?.residentName || process?.resident?.name || process?.resident?.fullName || process?.resident || "Unnamed resident";

export const processResidentId = (process) =>
  process?.residentId || process?.resident?.id || process?.resident?.residentId || process?.residentSlug || "";

export const processSocialWorkerName = (process) =>
  process?.socialWorkerName ||
  process?.socialWorker?.name ||
  [process?.socialWorkerFirstName || process?.socialWorker?.firstName, process?.socialWorkerLastName || process?.socialWorker?.lastName]
    .filter(Boolean)
    .join(" ") ||
  process?.socialWorker ||
  "Not assigned";

export const processRecords = (process) => {
  const records = process?.attachedRecords || process?.records || process?.assessmentRecords || [];
  if (Array.isArray(records)) return records;
  if (typeof records === "string" && records.trim()) {
    return records.split(",").map((record) => record.trim()).filter(Boolean);
  }
  return [];
};

export const recordLabel = (record) =>
  typeof record === "string" ? record : record?.name || record?.title || record?.recordName || record?.recordType || "Assessment Record";

export const recordSlug = (record) =>
  typeof record === "string" ? assessmentRecordSlug(record) : record?.slug || record?.recordType || record?.type || assessmentRecordSlug(recordLabel(record));

const dateInputValue = (date) => new Date(date).toISOString().slice(0, 10);

const shiftDate = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const probationYesterday = dateInputValue(shiftDate(new Date(), -1));
const probationTwoDaysAgo = dateInputValue(shiftDate(new Date(), -2));
const probationOlderDay = dateInputValue(shiftDate(new Date(), -5));

export const assessmentRecordSlug = (record) =>
  String(record || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const assessmentRecordDetails = {
  "ap-new-mj-2024": {
    "initial-assessment": {
      document: "State Initial Assessment.pdf",
      dateUploaded: "2024-05-06",
      uploadedBy: "Sarah Mitchell",
      socialWorker: "Amanda Parker",
      issuer: "Washington DSHS",
      notes: "<p><strong>Initial state assessment received.</strong></p><ul><li>Medication behavior, diagnosis, history, care needs, and client profile were included.</li><li>Provider review should compare state details against Meet and Greet findings.</li></ul>",
    },
    "meet-and-greet": {
      meetingDate: "2024-05-06",
      location: "Harborview Medical Center, Room 412",
      socialWorker: "Amanda Parker",
      facilitator: "Sarah Mitchell",
      careProviderStaff: ["Sarah Mitchell - Lead caregiver", "Daniel Reed - Care aide"],
      careCircle: [
        "Elaine Johnson - Daughter / PoA",
        "Robert Johnson - Son",
        "Maya Torres - Care taker",
        "Linda Green - Guardian contact",
      ],
      signedDocuments: ["Meet and Greet acknowledgement"],
      uploadedDocuments: ["Hospital discharge summary.pdf", "Medication list.pdf"],
      notes: "<p>Resident was <strong>alert, calm, and receptive</strong> to a home visit.</p><ul><li>Family emphasized mobility support and medication routines.</li><li>Care team should confirm transfer support needs during onboarding.</li></ul>",
      questions: [
        ["What daily routines make you feel most comfortable?", "Morning coffee, quiet breakfast, and afternoon phone call with daughter."],
        ["What support do you need with transfers or walking?", "Needs one-person standby assist when tired."],
        ["Are there foods, activities, or care approaches you prefer?", "Low-salt meals, music after lunch, and door slightly open at night."],
      ],
    },
    "client-onboarding": {
      meetingDate: "2024-05-08",
      location: "Olreadia Care Home, Maple Suite",
      socialWorker: "Amanda Parker",
      facilitator: "Sarah Mitchell",
      careProviderStaff: ["Sarah Mitchell - Lead caregiver", "Daniel Reed - Care aide", "Maya Torres - Medication aide"],
      careCircle: [
        "Elaine Johnson - Daughter / PoA",
        "Robert Johnson - Son",
        "Maya Torres - Care taker",
        "Linda Green - Guardian contact",
      ],
      signedDocuments: [
        "Admission agreement",
        "Resident rights and responsibilities",
        "Medication management consent",
        "HIPAA/privacy acknowledgment",
        "House policies",
      ],
      uploadedDocuments: ["Signed admission packet.pdf", "PoA documentation.pdf", "Medication consent.pdf"],
      notes: "<p><strong>Client onboarding completed at the home.</strong></p><ul><li>Family reviewed the house policies and resident daily rhythm.</li><li>Medication storage, meal preferences, and first-week observation plan were confirmed.</li></ul>",
      questions: [
        ["What daily routines should staff follow from the first day?", "Morning coffee by 8:00 AM, light breakfast, and family call after dinner."],
        ["What items, equipment, or comfort preferences came with the resident?", "Walker, preferred blanket, framed family photo, and medication organizer."],
        ["Who should staff contact for urgent family updates?", "Elaine Johnson, daughter and PoA."],
      ],
    },
    "resident-30-day-probation": {
      startDate: probationTwoDaysAgo,
      endDate: dateInputValue(shiftDate(new Date(), 28)),
      status: "Active observation",
      description:
        "Thirty-day probation observation period for behavior, health, welfare, medication routines, care needs, and discrepancies between state assessment, Meet and Greet, and actual home routines.",
      dailyNotes: [
        {
          noteDate: probationYesterday,
          date: `${probationYesterday}T16:30:00`,
          title: "Evening transition note",
          addedBy: "Daniel Reed",
          updatedBy: "Sarah Mitchell",
          updatedAt: `${probationYesterday}T17:05:00`,
          category: "Behavior and welfare",
          summary: "Resident was calm after dinner, requested family call, and used walker with standby support.",
          notes: "<p>Resident was calm after dinner and requested a family call.</p><ul><li>Used walker with standby support.</li><li>No medication refusal observed.</li></ul>",
          photos: [{ fileName: "walker-position.jpg", description: "Walker positioned near recliner before evening transfer." }],
        },
        {
          noteDate: probationTwoDaysAgo,
          date: `${probationTwoDaysAgo}T09:15:00`,
          title: "Morning care note",
          addedBy: "Sarah Mitchell",
          updatedBy: "Sarah Mitchell",
          updatedAt: `${probationTwoDaysAgo}T09:40:00`,
          category: "Health and medication",
          summary: "Medication routine completed on schedule. Mild fatigue observed during transfer from bed to chair.",
          notes: "<p>Medication routine completed on schedule.</p><p>Mild fatigue observed during transfer from bed to chair.</p>",
          photos: [],
        },
        {
          noteDate: probationOlderDay,
          date: `${probationOlderDay}T12:10:00`,
          title: "Meal preference update",
          addedBy: "Maya Torres",
          updatedBy: "Maya Torres",
          updatedAt: `${probationOlderDay}T12:20:00`,
          category: "Care preference",
          summary: "Resident prefers warm tea before lunch and needs reminders to use non-slip footwear.",
          notes: "<p>Resident prefers warm tea before lunch and responded well to gentle footwear reminders.</p>",
          photos: [],
        },
      ],
      assignedStaff: [
        { name: "Sarah Mitchell", role: "Lead caregiver", from: `${probationYesterday}T07:00:00`, to: `${probationYesterday}T15:00:00` },
        { name: "Daniel Reed", role: "Care aide", from: `${probationYesterday}T15:00:00`, to: `${probationYesterday}T23:00:00` },
        { name: "Maya Torres", role: "Medication aide", from: `${probationTwoDaysAgo}T08:00:00`, to: `${probationTwoDaysAgo}T12:00:00` },
      ],
    },
    "preliminary-plan": {
      status: "Draft",
      templateName: "Tab 15_4 Preliminary Service Plan (1) (2).docx",
      createdAt: `${probationTwoDaysAgo}T13:15:00`,
      createdBy: "Sarah Mitchell",
      latestGeneratedAt: `${probationYesterday}T09:30:00`,
      latestGeneratedBy: "Olreadia Care Assistant",
      lastUpdatedAt: `${probationYesterday}T15:45:00`,
      lastUpdatedBy: "Sarah Mitchell",
      generationLocked: false,
      description:
        "<h3>Preliminary Service Plan</h3><p><strong>Generated from state assessment, Meet and Greet, onboarding inputs, and early observation notes.</strong></p><h4>Resident Profile and Responsible Parties</h4><p>Resident: Margaret Johnson. Responsible party/PoA: Elaine Johnson, daughter. Other involved family/care circle members include Robert Johnson and Linda Green. Confirm age, birth date, move-in date, spouse/family history, siblings, children, grandchildren, close friends, and preferred family contact details during the next family meeting.</p><h4>Personal History, Religion, Interests, and Social Preferences</h4><p>Resident prefers quiet mornings, coffee, music after lunch, and a family call after dinner. Staff should learn more about important celebrations, religious/denomination preferences, church visits, communion needs, pets, hobbies, group preferences, favorite music, and whether resident leaves the home for special events.</p><h4>Food Preferences and Dietary Needs</h4><p>Resident prefers low-salt meals, warm tea before lunch, and light breakfast routines. Confirm breakfast/lunch/dinner preferred times, favorite snacks, dining-room versus bedroom preference, alcohol permission, specialized diet orders, favorite foods, dislikes, beverages, meats, vegetables, fruits, and comfort-food recipes.</p><h4>Communication, Sensory, and Safety</h4><p>Resident communicates verbally and responds best to calm, respectful cues. Confirm speech, hearing, eyesight, telephone use, preferred language, need for speech therapy assessment, bedroom lock preference, privacy curtain preference, allergies, and evacuation level. Resident requires standby assistance and clear walkways for evacuation.</p><h4>Medication, Treatments, and Health Monitoring</h4><p>Medication routine requires prompts and monitoring for refusal. Confirm self-administration status, nurse delegation, injections, oxygen, pain issues, community programs, vital signs, blood sugar checks, weight monitoring, wound care, home health, PT, OT, speech therapy, RN support, and hospice involvement.</p><h4>Mental Capacity, Attitude, and Behavior</h4><p>Resident has been calm and receptive in early notes. Continue documenting mood, orientation, confusion, agitation, forgetfulness, wandering, elopement risk, door alarm need, psycho-pharmacological medication use, location checks, and any behaviors from the state assessment with matching interventions.</p><h4>Mobility, Transfers, Bed Mobility, and Equipment</h4><p>Resident uses walker with standby support and has mild transfer fatigue. Keep walker within reach near recliner and bed. Confirm ambulation level, wheelchair/cane/crutches use, one-person or two-person assist, transfer method, lift needs, paralysis, restorative range of motion, bed repositioning frequency, and equipment such as hospital bed, gait belt, transfer board, bath bench, oxygen, pressure mattress, blood pressure machine, and O2 saturation meter.</p><h4>Continence, Toileting, Bathing, Dressing, Skin, Grooming, Mouth Care, and Eating</h4><p>Continue observing continence, toileting routine, bowel movement frequency, dressing support, bath type and bath days, skin care, contractures or pressure areas, grooming, podiatry, dentures/mouth care, feeding ability, clothing protector needs, special equipment during meals, fluid restriction or encouragement, thickened liquids, and hydration schedule.</p><h4>Goals, Preferences, and Refusal Plan</h4><p>Resident goals and preferences should be confirmed with resident and family. If medication is refused, notify PCP at the time of refusal and document the event. Ask whether other refusal behaviors occur and document how staff should manage them using the least restrictive, resident-centered approach.</p>",
      dataSources: [
        "Initial Assessment",
        "Meet and Greet",
        "Client Onboarding",
        "Resident 30-Day Probation",
        "Daily Notes",
      ],
      attachedFiles: [
        { type: "document", name: "Tab 15_4 Preliminary Service Plan (1) (2).docx", caption: "Preliminary service plan source template" },
        { type: "document", name: "Preliminary care plan draft.pdf", caption: "Generated preliminary plan draft" },
        { type: "photo", name: "walker-position.jpg", caption: "Transfer support photo from probation note" },
        { type: "document", name: "Medication list.pdf", caption: "Medication source file used for plan generation" },
      ],
    },
    "annual-residency-progress": {
      startDate: "2024-05-08",
      endDate: "2025-05-08",
      status: "Active annual progress",
      description:
        "Annual residency progress period for ongoing resident care patterns, status changes, care coordination, daily notes, family updates, and new items that may affect the negotiated care plan.",
      evaluationNotes:
        "<p><strong>Overall evaluation/status notes:</strong> Resident remains stable with continued need for calm routines, medication prompts, transfer support, and family communication after dinner.</p><ul><li>No urgent welfare concerns noted in the current progress window.</li><li>Continue monitoring transfer fatigue and hydration reminders.</li><li>Daily notes should be reviewed before updating the negotiated care plan.</li></ul>",
      dailyNotes: [
        {
          noteDate: probationYesterday,
          date: `${probationYesterday}T18:20:00`,
          title: "Annual progress daily status",
          addedBy: "Sarah Mitchell",
          updatedBy: "Sarah Mitchell",
          updatedAt: `${probationYesterday}T18:45:00`,
          category: "Welfare",
          summary: "Resident remained engaged during evening routine and completed family call without distress.",
          notes: "<p>Resident remained engaged during evening routine and completed family call without distress.</p><ul><li>Hydration reminders were accepted.</li><li>No new medication concerns reported.</li></ul>",
          photos: [],
        },
        {
          noteDate: probationTwoDaysAgo,
          date: `${probationTwoDaysAgo}T10:30:00`,
          title: "Mobility observation",
          addedBy: "Daniel Reed",
          updatedBy: "Maya Torres",
          updatedAt: `${probationTwoDaysAgo}T11:05:00`,
          category: "Mobility",
          summary: "Resident used walker with standby assist and reported mild fatigue after breakfast.",
          notes: "<p>Resident used walker with standby assist and reported mild fatigue after breakfast.</p>",
          photos: [{ fileName: "walker-check.jpg", description: "Walker placed within reach near breakfast table." }],
        },
      ],
      assignedStaff: [
        { name: "Sarah Mitchell", role: "Lead caregiver", from: `${probationYesterday}T07:00:00`, to: `${probationYesterday}T15:00:00` },
        { name: "Daniel Reed", role: "Care aide", from: `${probationYesterday}T15:00:00`, to: `${probationYesterday}T23:00:00` },
      ],
    },
    "negotiated-plan": {
      status: "Draft",
      templateName: "AFH HCS NCP-Template 10.11.23.docx",
      formVersion: "3.12.2024",
      createdAt: `${probationYesterday}T10:15:00`,
      createdBy: "Sarah Mitchell",
      latestGeneratedAt: `${probationYesterday}T11:05:00`,
      latestGeneratedBy: "Olreadia Care Assistant",
      lastUpdatedAt: `${probationYesterday}T12:20:00`,
      lastUpdatedBy: "Sarah Mitchell",
      dataSources: [
        "Initial Assessment",
        "Meet and Greet",
        "Client Onboarding",
        "Resident 30-Day Probation",
        "Annual Residency Progress",
        "Preliminary Plan",
        "Daily Notes",
      ],
      sections: [
        {
          key: "resident-profile",
          title: "Resident and Provider Information",
          helper: "Provider name, NCP dates, moved-in date, resident name, pronouns, DOB/age, primary language, allergies, legal documents, and specialty needs.",
          value:
            "Provider: Olreadia Care. Resident: Margaret Johnson. Primary language: English. Interpreter needed: No. Allergies and legal document status should be verified against the state assessment and admission packet before finalization.",
        },
        {
          key: "evacuation-safety",
          title: "Emergency Evacuation and Safety Instructions",
          helper: "Evacuation assistance required, safety instructions, and caregiver evacuation responsibilities.",
          value:
            "Assistance required. Resident needs standby assistance, walker access, clear walkways, and calm cueing during evacuation. Caregiver will keep evacuation routes clear and guide resident using short, direct instructions.",
        },
        {
          key: "medical-status",
          title: "Mental/Physical Health - Current Medical Status/Diagnosis",
          helper: "Current diagnosis, medical status, allergies, health indicators, pain, and other issues or concerns.",
          value:
            "State assessment and early care notes indicate transfer fatigue, medication prompting needs, and ongoing monitoring for hydration, mobility, welfare, and changes in behavior. No acute welfare concerns noted during the current observation window.",
        },
        {
          key: "responsible-parties",
          title: "Responsible Parties - Contacts",
          helper: "Case manager, DPOA, guardian, medical providers, pharmacy, hospice, and preferred contact methods.",
          value:
            "Amanda Parker, Social Worker. Elaine Johnson, Daughter / PoA, preferred family contact. Robert Johnson, Son. Care team should confirm phone, email, pharmacy, PCP, and other provider contact details before completion.",
        },
        {
          key: "communication",
          title: "Communication, Speech, Hearing, and Vision",
          helper: "Modes of expression, hearing/vision concerns, phone use, preferred language, strengths, preferences, assistance, and caregiver instructions.",
          value:
            "Resident communicates needs verbally and benefits from calm, respectful explanations. Resident prefers family calls after dinner and responds well to gentle reminders. No interpreter need has been identified.",
        },
        {
          key: "medication-management",
          title: "Medication Management",
          helper: "Medication assistance type, MAR reference, pharmacy packing, nurse delegation, refusal plan, and caregiver instructions.",
          value:
            "Resident requires medication prompts and monitoring for refusal or adverse reactions. Caregiver will follow physician orders, MAR documentation, the five rights plus documentation, and report significant concerns immediately.",
        },
        {
          key: "treatments-programs",
          title: "Treatments, Programs, Therapies, and Health Indicators",
          helper: "Pain, weight, vital signs, therapies, home health, hospice, meaningful day, physical enablers, and refusal plan.",
          value:
            "Continue monitoring mobility, transfer fatigue, hydration, and any treatment or therapy instructions received from PCP, PT, OT, or nurse delegation. Meaningful day activities should remain part of the support plan.",
        },
        {
          key: "psych-social-cognitive",
          title: "Psych/Social/Cognitive Status",
          helper: "Sleep, memory, decision making, behaviors, crisis plan, mental health supports, interventions, left-alone status, and narrative day.",
          value:
            "Resident benefits from a predictable routine, quiet breakfast, music after lunch, and family contact. No disruptive or unsafe behavior has been observed in current notes, but staff should document changes promptly.",
        },
        {
          key: "adls",
          title: "Activities of Daily Living",
          helper: "Ambulation, transfers, eating, toileting, dressing, hygiene, bathing, foot care, skin care, strengths, preferences, assistance, and caregiver instructions.",
          value:
            "Resident uses walker with standby support and may experience mild transfer fatigue. Caregiver will maintain walker within reach, cue use of non-slip footwear, support transfers, and monitor for falls, skin concerns, and fatigue.",
        },
        {
          key: "iadls-activities",
          title: "Instrumental Activities, Social, Transportation, and Case Management",
          helper: "Finances, shopping, transportation, activities, smoking, case management, other issues, and resident recommendations.",
          value:
            "Resident enjoys quiet routines, warm tea before lunch, music after lunch, and family contact. Transportation, shopping, finances, and case-management details should be confirmed with PoA and social worker.",
        },
        {
          key: "review-approval",
          title: "Negotiated Care Plan Review and Approval",
          helper: "Original plan date, review/revision dates, people involved, approvals, signatures, verbal agreement, DSHS CM sent date, and resident recommendations.",
          value:
            "Resident and resident representative should participate to the extent possible. Final plan should be reviewed with provider, resident/representative, and DSHS case manager before confirmation and signature collection.",
        },
      ],
      attachedFiles: [
        { type: "document", name: "AFH HCS NCP-Template 10.11.23.docx", caption: "Locked source template" },
      ],
    },
  },
};

export default function AssessmentsPage() {
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    careService.assessmentProcesses
      .list()
      .then((data) => {
        if (!isMounted) return;
        setProcesses(Array.isArray(data) ? data : []);
        setError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setProcesses([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load assessment processes.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const summary = useMemo(() => {
    const active = processes.filter((process) => !["Confirmed", "Closed"].includes(process.status)).length;
    const newResidents = processes.filter((process) => process.patientType === "NEW").length;
    const currentResidents = processes.filter((process) => process.patientType === "RESIDENT").length;
    return { active, newResidents, currentResidents };
  }, [processes]);

  const selectedResidentId = processResidentId(selectedProcess);

  return (
    <CareShell className="dashboard-content residents-page assessments-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Assessments</h1>
          <p>Track every assessment process from state assessment through final assessment and residency confirmation.</p>
        </div>
        <div className="residents-actions">
          <button className="mini-btn"><i className="ti ti-filter" /> Filter</button>
          <Link className="btn primary" to="/assessments/new">
            <i className="ti ti-clipboard-plus" /> Start New Assessment Process
          </Link>
        </div>
      </div>

      <div className="resident-summary-grid">
        {[
          ["ti-clipboard-text", "Total processes", processes.length, "In this workspace"],
          ["ti-player-play", "Active", summary.active, "Need action"],
          ["ti-user-plus", "New residents", summary.newResidents, "Onboarding flows"],
          ["ti-user-check", "Current residents", summary.currentResidents, "Continuing care reviews"],
        ].map(([icon, label, value, note]) => (
          <article className="resident-summary-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div><strong>{label}</strong><b>{value}</b><small>{note}</small></div>
          </article>
        ))}
      </div>

      <Panel title="Assessment Processes">
        <div className="resident-list-toolbar">
          <label className="resident-search">
            <i className="ti ti-search" />
            <input placeholder="Search by resident, social worker, status, or attached record" />
          </label>
          <div className="resident-filter-pills">
            <button className="is-active">All</button>
            <button>New Resident</button>
            <button>Current Resident</button>
            <button>Active</button>
          </div>
        </div>
        {error ? <p className="form-error">{error}</p> : null}

        <div className="assessment-process-table">
          <div className="assessment-process-row assessment-process-head">
            <span>Resident Name</span>
            <span>Resident Type</span>
            <span>Start Date</span>
            <span>Social Worker</span>
            <span>Status</span>
            <span />
          </div>
          {loading ? <p className="assessment-empty-state">Loading assessment processes...</p> : null}
          {!loading && !processes.length && !error ? <p className="assessment-empty-state">No assessment processes found.</p> : null}
          {processes.map((process) => (
            <button className="assessment-process-row" type="button" onClick={() => setSelectedProcess(process)} key={process.id}>
              <span>
                <strong>{processResidentName(process)}</strong>
                <small>{process.name || process.title || "Assessment process"}</small>
              </span>
              <span><em className="pill blue">{process.patientType || process.type}</em></span>
              <span><strong>{formatDate(process.startDate || process.processStartDate)}</strong></span>
              <span>{processSocialWorkerName(process)}</span>
              <span><em className={`pill ${processStatusClass(process.status)}`}>{process.status}</em></span>
              <span className="resident-row-actions"><i className="ti ti-chevron-right" /></span>
            </button>
          ))}
        </div>
      </Panel>
      <Drawer
        title={selectedProcess?.name}
        placement="right"
        open={Boolean(selectedProcess)}
        onClose={() => setSelectedProcess(null)}
        width={720}
        className="resident-action-drawer"
      >
        {selectedProcess ? (
          <div className="resident-drawer-content">
            <div className="assessment-drawer-action-links">
              <Link to={selectedResidentId ? `/residents/${selectedResidentId}#assessment` : "/residents"}><i className="ti ti-user" /> Resident</Link>
              <Link to={`/assessments/${selectedProcess.id}`}><i className="ti ti-settings" /> Manage</Link>
              <button className="success" type="button"><i className="ti ti-clipboard-plus" /> Add Assessment</button>
              <button type="button"><i className="ti ti-edit" /> Edit</button>
              <button className="danger" type="button"><i className="ti ti-trash" /> Delete</button>
              <button className="warning" type="button"><i className="ti ti-ban" /> Cancel</button>
            </div>
            {/* <p>Assessment process details for {selectedProcess.resident}.</p> */}
            <section className="assessment-drawer-section">
              <h3>Assessment Records</h3>
              <div className="assessment-record-link-grid">
                {processRecords(selectedProcess).map((record) => (
                  <Link to={`/assessments/${selectedProcess.id}/records/${recordSlug(record)}`} key={recordSlug(record)}>
                    <i className="ti ti-file-description" />
                    <span>{recordLabel(record)}</span>
                  </Link>
                ))}
              </div>
            </section>
            <section className="assessment-drawer-section">
              <h3>Process Details</h3>
              <div className="assessment-detail-grid">
                <div><span>Start date</span><strong>{formatDate(selectedProcess.startDate || selectedProcess.processStartDate)}</strong></div>
                <div><span>End date</span><strong>{formatDate(selectedProcess.endDate || selectedProcess.processEndDate)}</strong></div>
                <div><span>Status</span><strong>{selectedProcess.status}</strong></div>
                <div><span>Patient type</span><strong>{selectedProcess.patientType}</strong></div>
              </div>
            </section>
            <section className="assessment-drawer-section">
              <h3>Resident Details</h3>
              <div className="assessment-detail-grid">
                <div><span>Resident name</span><strong>{processResidentName(selectedProcess)}</strong></div>
                <div><span>Resident type</span><strong>{selectedProcess.patientType === "NEW" ? "New Resident" : "Current Resident"}</strong></div>
              </div>
            </section>
            <section className="assessment-drawer-section">
              <h3>Social Worker</h3>
              <div className="assessment-detail-grid">
                <div><span>First name</span><strong>{selectedProcess.socialWorkerFirstName || selectedProcess.socialWorker?.firstName || processSocialWorkerName(selectedProcess).split(" ")[0]}</strong></div>
                <div><span>Last name</span><strong>{selectedProcess.socialWorkerLastName || selectedProcess.socialWorker?.lastName || processSocialWorkerName(selectedProcess).split(" ").slice(1).join(" ")}</strong></div>
                <div><span>Email</span><strong>{selectedProcess.socialWorkerEmail || selectedProcess.socialWorker?.email || "Not set"}</strong></div>
                <div><span>Phone</span><strong>{selectedProcess.socialWorkerPhone || selectedProcess.socialWorker?.phone || "Not set"}</strong></div>
                <div className="wide"><span>Address</span><strong>{selectedProcess.socialWorkerAddress || selectedProcess.socialWorker?.address || "Not set"}</strong></div>
              </div>
            </section>
            <section className="assessment-drawer-section">
              <h3>Next of Kin</h3>
              <div className="assessment-detail-grid">
                <div><span>First name</span><strong>{selectedProcess.nextOfKinFirstName || selectedProcess.nextOfKin?.firstName || "Not set"}</strong></div>
                <div><span>Last name</span><strong>{selectedProcess.nextOfKinLastName || selectedProcess.nextOfKin?.lastName || "Not set"}</strong></div>
                <div><span>Email</span><strong>{selectedProcess.nextOfKinEmail || selectedProcess.nextOfKin?.email || "Not set"}</strong></div>
                <div><span>Phone</span><strong>{selectedProcess.nextOfKinPhone || selectedProcess.nextOfKin?.phone || "Not set"}</strong></div>
                <div><span>Relationship</span><strong>{selectedProcess.nextOfKinRelationship || selectedProcess.nextOfKin?.relationship || "Not set"}</strong></div>
                <div><span>Address</span><strong>{selectedProcess.nextOfKinAddress || selectedProcess.nextOfKin?.address || "Not set"}</strong></div>
              </div>
            </section>
          </div>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
